import React, { useState, useEffect, useContext } from 'react'

import { useSavedSearch } from '../../../hooks/saved-search/saved-search-hook'
import { useForm } from '../../../forms/hocs/form.context'
import { Alert } from '../../../__generated__/fed-graphql-types'
import { validNameCharsRegEx, generateName } from './save-search-name-generator'
import { StyledSaveSearchModalTitle, StyledSaveSearchModalWrapper } from './save-search-form.styles'
import { SavedSearchQueryContext } from '../../../contexts/saved-search-context'
import { CanonicalSearchFinanceOption, CanonicalSearchRangeValues } from '../../../patterns/saved-searches/save-search'
import { UserLocationContext } from '../../../contexts/user-location-context'
import { addPostcodeIfNotInUrl } from '../../../hooks/saved-search/save-search-compare.hook'
import { useToastContext } from '../../../contexts/toast-context'
import { IconSavedOn } from '@at/design-system-icons'
import { CheckYourInboxComponent } from './check-your-inbox'

import { FirstVerify } from './first-verify'
import { AlreadySavedThisSearch } from './already-saved-this-search'
import { SaveThisSearch } from './save-this-search'
import { useUserState } from '@at/connected-signin'
import Cookies from 'js-cookie'

export type SearchValue = string[] & CanonicalSearchRangeValues & CanonicalSearchFinanceOption

export type AddSaveErrorType = { isError: boolean; errorMessage: string }

interface SaveSearchFormProps {
    handleHideModal: () => void
}

export enum MODAL_MODE {
    SAVE_SEARCH,
    FIRST_VERIFY,
    CHECK_YOUR_INBOX,
}

export const SaveSearchForm = ({ handleHideModal }: SaveSearchFormProps) => {
    const [mode, setMode] = useState(MODAL_MODE.SAVE_SEARCH)
    const { selectedMap } = useForm()
    const { saveSearchOrSignIn } = useSavedSearch()
    const [, setUserState] = useUserState()
    const { postcode: contextPostcode } = useContext(UserLocationContext)
    const {
        savedSearchPersonalData,
        foundMatchingSavedSearch: foundMatchingSavedSearch,
        sendVerificationEmail,
    } = useContext(SavedSearchQueryContext)
    const [addSaveSearchError, setAddSaveSearchError] = useState<AddSaveErrorType>({ isError: false, errorMessage: '' })
    const generatedName = generateName(selectedMap)
    const [searchName, setSearchName] = useState(generatedName)
    const [alert, setAlert] = useState(Alert.OFF)
    const addToast = useToastContext()

    useEffect(() => {
        setSearchName(generatedName)
    }, [setSearchName, generatedName])

    const clearErrorMessage = () => setAddSaveSearchError({ isError: false, errorMessage: '' })

    const saveSearch = async (
        name: string,
        searchUrl: string,
        alert: Alert,
        message = 'This search has been saved.'
    ) => {
        /** Add postcode from UserLocationContext if is not present in the URL  */
        // TODO: fix logic when latlong is selected
        const urlWithPostcode = addPostcodeIfNotInUrl(searchUrl, contextPostcode || '')

        setUserState({
            userSignedIn: !!Cookies.get('ursid'),
        })

        try {
            handleHideModal()
            await saveSearchOrSignIn(name, urlWithPostcode, alert)
            addToast({
                message,
                icon: <IconSavedOn title="Search saved" fill="red" customSize={{ height: 14, width: 14 }} />,
                link: '/secure/saved-searches',
                linkText: 'View saved list',
            })
        } catch (error) {
            const errorMessage = (error as Record<string, string>).message
            setAddSaveSearchError({
                isError: true,
                errorMessage: errorMessage,
            })
        }
    }

    const onSaveClick = async (name: string, searchUrl: string, alert: Alert) => {
        if (mode === MODAL_MODE.SAVE_SEARCH && savedSearchPersonalData.verified === false && alert === Alert.ON) {
            setMode(MODAL_MODE.FIRST_VERIFY)
        } else {
            await saveSearch(name, searchUrl, alert)
        }
    }

    const onNameChange = (value: string): void => {
        const parsedValue = value.trimStart().replace(/\s\s+/g, ' ')
        const result = new RegExp(`^[${validNameCharsRegEx}]+$`)
        clearErrorMessage()
        setSearchName(parsedValue)

        if (parsedValue && !result.test(parsedValue)) {
            setAddSaveSearchError({
                isError: true,
                errorMessage:
                    'must only contain letters, numbers, hyphens, underscores, spaces, full-stops, commas, pound signs.',
            })
        }
    }

    if (mode === MODAL_MODE.SAVE_SEARCH) {
        return (
            <StyledSaveSearchModalWrapper data-testid="saved-search-modal">
                <>
                    <StyledSaveSearchModalTitle as="h3">Save this search</StyledSaveSearchModalTitle>
                    {foundMatchingSavedSearch ? (
                        <AlreadySavedThisSearch />
                    ) : (
                        <SaveThisSearch
                            alert={alert}
                            setAlert={setAlert}
                            addSaveSearchError={addSaveSearchError}
                            searchName={searchName}
                            onNameChange={onNameChange}
                            onSaveClick={onSaveClick}
                        />
                    )}
                </>
            </StyledSaveSearchModalWrapper>
        )
    }
    if (mode === MODAL_MODE.FIRST_VERIFY) {
        return (
            <FirstVerify
                sendVerificationEmail={sendVerificationEmail}
                setMode={setMode}
                savedSearchPersonalData={savedSearchPersonalData}
            />
        )
    }
    if (mode === MODAL_MODE.CHECK_YOUR_INBOX) {
        return <CheckYourInboxComponent saveSearch={saveSearch} searchName={searchName} alert={alert} />
    }
    return <></>
}
